
import React, { useState, useEffect, useMemo } from 'react'
import { Box, Typography, Skeleton } from '@mui/material'
import { makeStyles } from '@mui/styles';

import { getBatchPerformanceOverview, getAverageAttemptRateDetails } from '../../services/analyticsV2';
import { CASE_CATEGORY_MAX, InterviewVariantTypes, isCaseVariant } from './constant';
import BatchScoreGraph from 'components/LinearBarGraph'
import NoDataPanel from 'components/Analytics/NoDataPanel'
import ScoreDistributionChart from 'components/Analytics/ScoreDistribution';
import { AttemptRateChart } from 'components/Analytics/ScoreDistribution'

const getTrendsLabels = (type) => {
  switch (type) {
    case 'assessment':
      return {
        title: 'Assessment Trends',
        subtitle: 'Monitor batch readiness through published assessments'
      };
    case 'coding_mock':
      return {
        title: 'Coding Mock Trends',
        subtitle: 'Monitor batch readiness through published coding mocks'
      };
    case 'coding_iw':
      return {
        title: 'Coding Mock Trends',
        subtitle: 'Monitor batch readiness through published coding mocks'
      };
    case 'verbal_mock':
      return {
        title: 'Next Round Mock Trends',
        subtitle: 'Monitor batch readiness through published mocks'
      };
    case 'verbal_iw':
      return {
        title: 'Next Round Interview Trends',
        subtitle: 'Monitor batch readiness through published interviews'
      };
    case 'case_mock':
      return {
        title: 'Next Round Case Mock Trends',
        subtitle: 'Monitor batch readiness through published mocks'
      };
    case 'case_iw':
      return {
        title: 'Case-Study Mock Trends',
        subtitle: 'Monitor batch readiness through published mocks'
      };
    default:
      return {
        title: 'Next Round Mock Trends',
        subtitle: 'Monitor batch readiness through published mocks'
      };
  }
};

// One per case category, in the order the API returns them.
const CASE_BAR_COLORS = ['#6E5DC6', '#52B8CC', '#1151A6', '#737DA8', '#D46A00', '#9DB2F2'];

const useStyles = makeStyles((theme) => ({
    leftSection: {
        width: '50%',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column'
      },
      metricsContainer: {
        display: 'flex',
        gap: '16px',
        width: '100%'
      },
      chartContainer: {
        display: 'flex',
        gap: '70px',
        padding: '20px',
        backgroundColor: '#FCFAFA',
        borderRadius: '12px'
      }
}))

const TrendsSkeleton = ({ interviewVariantType }) => {
  const trendsLabels = getTrendsLabels(interviewVariantType);

  return (
    <Box display='flex' flexDirection='column' gap='24px'>
      <Box display='flex' flexDirection='column' gap='4px'>
        <Typography variant="h4-medium">
          {trendsLabels.title}
        </Typography>
        <Typography variant='h6-medium' color='neutral.clr-600'>
          {trendsLabels.subtitle}
        </Typography>
      </Box>
      <Box display='flex' gap='20px' width='100%' justifyContent='space-between'>
        <Skeleton variant="rectangular" width="50%" height={400} />
        <Box display='flex' flexDirection='column' gap='24px' width="50%">
          <Skeleton variant="rectangular" width="100%" height={180} />
          <Skeleton variant="rectangular" width="100%" height={180} />
        </Box>
      </Box>
    </Box>
  );
};

const Trends = ({ batchId, interviewVariantType = InterviewVariantTypes.VERBAL_MOCK }) => {
    const classes = useStyles();
    const [performanceData, setPerformanceData] = useState({
      communicationAverage: 0,
      contentAverage: 0,
      totalAttempts: 0
    });
    const [attemptRateData, setAttemptRateData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoading(true);
          
          const [performanceResponse, attemptRateResponse] = await Promise.all([
            getBatchPerformanceOverview(batchId, interviewVariantType),
            getAverageAttemptRateDetails({ type: 'batch', id: batchId, interviewVariantType })
          ]);
          setPerformanceData(performanceResponse || {
            communicationAverage: 0,
            contentAverage: 0,
            totalAttempts: 0
          });
          
          setAttemptRateData(attemptRateResponse || {});
          setError(null);
        } catch (err) {
          setError(err.message);
          console.error('Error fetching trends data:', err);
        } finally {
          setLoading(false);
        }
      };

      if (batchId) {
        fetchData();
      }
    }, [batchId, interviewVariantType]);

    const safePerformanceData = useMemo(() => (
      error
        ? {
            communicationAverage: 0,
            contentAverage: 0,
            totalAttempts: 0,
            coding: { overall: 0 },
            coreConcepts: { overall: 0 },
            criticalThinking: { overall: 0 },
            content: { overall: 0 },
            speech: { overall: 0 },
            categories: []
          }
        : performanceData
    ), [error, performanceData]);

    const safeAttemptRateData = useMemo(() => (
      error
        ? {
            averageScoreStats: {
              averageScore: 0,
              "0-30": 0,
              "31-60": 0,
              "61-100": 0,
              "1.2-2.33": 0,
              "2.34-3.67": 0,
              "3.68-5.0": 0,
              scoreDistribution: []
            },
            attemptRateStats: {
              attemptRate: 0,
              "0-30": 0,
              "31-60": 0,
              "61-100": 0,
              attempted: 0,
              notAttempted: 0
            },
            totalStudents: 0
          }
        : attemptRateData
    ), [error, attemptRateData]);

    const graphData = useMemo(() => {
      const base = {
        dataValues: [],
        labels: [],
        barColors: ['#6E5DC6', '#52B8CC'],
        title: 'Performance Overview',
        legendLabels: []
      };

      const isCodingType = interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw';

      // The case API returns its own ordered category list, so the chart is
      // built from the payload rather than a hardcoded parallel array.
      if (isCaseVariant(interviewVariantType)) {
        const categories = safePerformanceData.categories || [];

        return {
          ...base,
          dataValues: categories.map((category) => category.overall || 0),
          labels: categories.map((category) => category.label),
          title: 'Batch average & score distribution',
          // Six category names will not fit along the axis without colliding.
          // The legend below names every bar, so the axis stays clean.
          showXLabels: false,
          barColors: CASE_BAR_COLORS.slice(0, categories.length),
          legendLabels: categories.map((category, index) => ({
            id: category.id,
            label: category.label,
            color: CASE_BAR_COLORS[index % CASE_BAR_COLORS.length],
          })),
          yAxisMax: CASE_CATEGORY_MAX,
        };
      }

      if (isCodingType) {
        const dataValues = [
          safePerformanceData.coding?.overall || 0,
          safePerformanceData.coreConcepts?.overall || 0,
          safePerformanceData.criticalThinking?.overall || 0,
          safePerformanceData.content?.overall || 0
        ];
        const labels = ['Coding', 'Core Concepts', 'Critical Thinking', 'Content'];
        const barColors = ['#6E5DC6', '#52B8CC', '#1151A6', '#737DA8'];
        const legendLabels = [
          { id: 'coding', label: 'Coding', color: '#6E5DC6' },
          { id: 'coreConcepts', label: 'Core Concepts', color: '#52B8CC' },
          { id: 'criticalThinking', label: 'Critical Thinking', color: '#1151A6' },
          { id: 'content', label: 'Content', color: '#737DA8' }
        ];

        if (interviewVariantType === 'coding_iw') {
          dataValues.push(safePerformanceData.speech?.overall || 0);
          labels.push('Speech');
          barColors.push('#D46A00');
          legendLabels.push({ id: 'speech', label: 'Speech', color: '#D46A00' });
        }

        return {
          ...base,
          dataValues,
          labels,
          barColors,
          legendLabels,
          yAxisMax: 5
        };
      }

      return {
        ...base,
        dataValues: [
          safePerformanceData.communicationAverage || 0,
          safePerformanceData.contentAverage || 0
        ],
        labels: ['Communication', 'Content'],
        legendLabels: [
          { id: 'communication', label: 'Communication', color: '#7c4dff' },
          { id: 'content', label: 'Content', color: '#4fc3f7' }
        ],
        yAxisMax: 100
      };
    }, [interviewVariantType, safePerformanceData]);

    const trendsLabels = useMemo(() => getTrendsLabels(interviewVariantType), [interviewVariantType]);

    const { avgDistribution, scoreDistribution, attemptRateStats, bands, attemptRateDistribution } = useMemo(() => {
      const isCodingType = interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw';

      if (isCaseVariant(interviewVariantType)) {
        const stats = safeAttemptRateData.averageScoreStats || {};
        const rate = safeAttemptRateData.attemptRateStats || {};

        return {
          avgDistribution: { batchAverageScore: stats.averageScore || 0 },
          // Bands carry their own counts, so nothing is keyed by range string.
          scoreDistribution: null,
          bands: stats.scoreDistribution || [],
          attemptRateStats: {
            attemptRate: rate.attemptRate || 0,
            userSubmittedCount: rate.attempted || 0,
            userNotSubmittedCount: rate.notAttempted || 0,
          },
          attemptRateDistribution: rate.attemptRateDistribution || [],
        };
      }

      if (isCodingType) {
        const avgDistribution = {
          batchAverageScore: safeAttemptRateData.averageScoreStats?.averageScore || 0
        };
        const scoreDistribution = {
          "1.2-2.33": safeAttemptRateData.averageScoreStats?.["1.2-2.33"] || 0,
          "2.34-3.67": safeAttemptRateData.averageScoreStats?.["2.34-3.67"] || 0,
          "3.68-5.0": safeAttemptRateData.averageScoreStats?.["3.68-5.0"] || 0
        };

        const attemptRateStats = {
          attemptRate: safeAttemptRateData.attemptRateStats?.attemptRate || 0,
          userSubmittedCount: (safeAttemptRateData.attemptRateStats?.["31-60"] || 0) + (safeAttemptRateData.attemptRateStats?.["61-100"] || 0),
          userNotSubmittedCount: safeAttemptRateData.attemptRateStats?.["0-30"] || 0
        };

        return { avgDistribution, scoreDistribution, attemptRateStats };
      }

      const avgDistribution = {
        batchAverageScore: safeAttemptRateData.averageScoreStats?.averageScore || 0
      };
      const scoreDistribution = {
        "0-30": safeAttemptRateData.averageScoreStats?.["0-30"] || 0,
        "31-60": safeAttemptRateData.averageScoreStats?.["31-60"] || 0,
        "61-100": safeAttemptRateData.averageScoreStats?.["61-100"] || 0
      };
      const attemptRateStats = {
        attemptRate: safeAttemptRateData.attemptRateStats?.attemptRate || 0,
        userSubmittedCount: (safeAttemptRateData.attemptRateStats?.["31-60"] || 0) + (safeAttemptRateData.attemptRateStats?.["61-100"] || 0),
        userNotSubmittedCount: safeAttemptRateData.attemptRateStats?.["0-30"] || 0
      };

      return { avgDistribution, scoreDistribution, attemptRateStats };
    }, [interviewVariantType, safeAttemptRateData]);
    
    // Interview Wizard work is self-driven, so there is no assigned quota to
    // report an attempt rate against — every wizard variant hides that chart.
    const isWizardVariant = ['verbal_iw', 'coding_iw', 'case_iw'].includes(
      interviewVariantType
    );

    if (loading) {
      return <TrendsSkeleton interviewVariantType={interviewVariantType} />;
    }

    // A batch with no finished attempts would otherwise render six bars at zero
    // and a 0.00 average, which reads as "everyone scored nothing" rather than
    // "nobody has attempted yet".
    const hasNoCaseData = isCaseVariant(interviewVariantType)
      && !(safePerformanceData.totalAttempts > 0);

    if (hasNoCaseData) {
      return (
        <Box display='flex' flexDirection='column' gap='24px'>
          <Box display='flex' flexDirection='column' gap='4px'>
            <Typography variant='h4-medium'>{trendsLabels.title}</Typography>
            <Typography variant='h6-medium' color='neutral.clr-600'>
              {trendsLabels.subtitle}
            </Typography>
          </Box>
          <NoDataPanel
            message='No case attempts yet'
            hint={interviewVariantType === 'case_iw'
              ? 'Scores will appear once students practise a case on their own.'
              : 'Scores will appear once students attempt a published case mock.'}
          />
        </Box>
      );
    }
  return (
    <Box display='flex' flexDirection='column' gap='24px'>
        <Box display='flex' flexDirection='column' gap='4px'>
            <Typography variant='h4-medium'>
                {trendsLabels.title}
            </Typography>
            <Typography variant='h6-medium' color='neutral.clr-600'>
                {trendsLabels.subtitle}
            </Typography>
        </Box>
        {/* Both columns need an explicit basis and minWidth:0. Flex items
            default to min-width:auto, so a chart with long category labels
            refuses to shrink and pushes the row past the viewport. */}
        <Box display='flex' gap='20px' width='100%' justifyContent='space-between' alignItems='flex-start'>
            <Box flex='1 1 0' minWidth={0} display='flex'>
              <BatchScoreGraph {...graphData} />
            </Box>
            <Box flex='1 1 0' minWidth={0} display='flex' flexDirection='column' gap='24px' >
                                 {isWizardVariant ? (
                   <ScoreDistributionChart
                     avgDistribution={avgDistribution}
                     scoreDistribution={scoreDistribution}
                     bands={bands}
                     totalStudents={safeAttemptRateData.totalStudents || 0}
                     classes={classes}
                     interviewVariantType={interviewVariantType}
                   />
                 ) : (
                   <>
                     <ScoreDistributionChart
                       avgDistribution={avgDistribution}
                       scoreDistribution={scoreDistribution}
                       bands={bands}
                       totalStudents={safeAttemptRateData.totalStudents || 0}
                       classes={classes}
                       interviewVariantType={interviewVariantType}
                     />
                     <AttemptRateChart
                         attemptRate={attemptRateStats}
                         distribution={attemptRateDistribution}
                         title={isCaseVariant(interviewVariantType)
                           ? 'Attempt rate & distribution'
                           : 'Attempt rate'}
                         totalStudents={safeAttemptRateData.totalStudents || 0}
                         classes={classes}
                     />
                   </>
                 )}
            </Box>
        </Box>
        
    </Box>
  )
}

export default Trends